/* =========================================================
   El porcentaje del proceso de actualización.

   Convive con `completeness()` de fields.ts y no la sustituye. Son dos
   preguntas distintas y hay que no confundirlas:

     completeness()  ¿faltan campos ROJOS? — decide si un site sale en
                     «fichas con rojos pendientes» y si el recordatorio
                     previo al cierre avisa. Tres campos por site.

     progreso()      ¿cuánto lleva hecho este socio? — es el número que
                     ve la gente. Cuenta TODO lo editable, más los
                     documentos de compliance de la organización.

   Qué entra en el 100 % de un socio:

     · los 27 campos editables de la organización
     · los 29 de cada uno de sus sites
     · los 5 de cada instructor afiliado a alguno de sus sites, contado
       una sola vez aunque esté en dos
     · un punto por cada documento de compliance, cumplido al verificarlo

   Y qué NO entra:

     · los campos marcados «no aplica». Sin esa salida, un socio sin fax y
       sin tercera línea de dirección no llegaría al 100 % en su vida,
       habiendo rellenado todo lo suyo.
     · las opciones fijas de un campo de lista, como «Aprendizaje en el
       aula», que están en todas las fichas y nadie eligió.
     · los campos condicionales cuya condición no se cumple: «Otro
       producto, ¿cuál?» solo cuenta si el socio marcó «Otro».
     · el compliance de una organización que no tiene documentos
       publicados: no suma ni al numerador ni al denominador.

   Se suma, no se promedia. Un site con veinte campos pendientes pesa más
   que uno con dos, que es lo que quiere decir «cuánto trabajo queda».
   ========================================================= */
import type {
  ComplianceSummary, Instructor, Organization, Site,
} from '@/types';
import { camposQueCuentan, cuentaEnEstaFicha, sinFijas, type EntidadNA } from '../../db/columns';

export type Progreso = {
  /** Campos con valor, más documentos verificados. */
  hechos: number;
  /** Lo anterior más lo que falta. Ya sin los «no aplica». */
  total: number;
  /** Redondeado. 100 cuando no queda nada que contar. */
  pct: number;
};

/** Las marcas «no aplica», indexadas como llegan del servidor. */
export type MarcasNA = Record<EntidadNA, Record<string, string[]>>;

export const SIN_MARCAS: MarcasNA = { organization: {}, site: {}, instructor: {} };

const VACIO: Progreso = { hechos: 0, total: 0, pct: 100 };

/** Un campo cuenta como hecho si tiene algo escrito.
 *
 *  El '0000' es un código postal de relleno que venía en el padrón de
 *  Autodesk para las fichas sin dato. Tiene valor pero no es un valor, así
 *  que no cuenta — igual que en completeness().
 *
 *  En los campos de lista se descuentan las opciones fijas. «Aprendizaje en
 *  el aula» está en las 134 organizaciones y nadie lo eligió: contarlo daría
 *  ese campo por resuelto en todas sin que ningún socio hubiera contestado. */
const tieneValor = (campo: string, v: unknown): boolean => {
  if (Array.isArray(v)) return sinFijas(campo, v).length > 0;
  return (v ?? '').toString().trim() !== '' && v !== '0000';
};

/** El porcentaje NUNCA dice 100 si queda algo pendiente.
 *
 *  Antes se redondeaba al entero más cercano, y en un socio grande —seis
 *  sedes, ciento y pico instructores— 263 de 264 daba 99,6 % y se enseñaba
 *  «100 %». El socio creía haber terminado, y el servidor, que validaba el
 *  visto bueno con ese mismo número, se lo aceptaba. Ahora se trunca y el
 *  100 solo aparece con todo hecho. */
export function porcentaje(hechos: number, total: number): number {
  if (!total || hechos >= total) return 100;
  return Math.min(99, Math.floor((hechos / total) * 100));
}

function cerrar(hechos: number, total: number): Progreso {
  return { hechos, total, pct: porcentaje(hechos, total) };
}

const sumar = (...partes: Progreso[]): Progreso =>
  cerrar(
    partes.reduce((n, p) => n + p.hechos, 0),
    partes.reduce((n, p) => n + p.total, 0),
  );

/** El progreso de una ficha suelta: organización, site o instructor. */
export function progresoFicha(
  entidad: EntidadNA,
  id: string,
  record: Record<string, unknown> | null | undefined,
  na: MarcasNA = SIN_MARCAS,
): Progreso {
  if (!record) return VACIO;
  const excusados = new Set(na[entidad]?.[id] ?? []);
  const campos = camposQueCuentan(entidad)
    .filter((c) => !excusados.has(c) && cuentaEnEstaFicha(c, record));
  return cerrar(campos.filter((c) => tieneValor(c, record[c])).length, campos.length);
}

/** Cada documento publicado vale un punto, y se cobra al verificarlo. */
export function progresoCompliance(resumen: ComplianceSummary | null | undefined): Progreso {
  if (!resumen || !resumen.count) return VACIO;
  return cerrar(resumen.verified, resumen.count);
}

/** El progreso de un site: su ficha y la de sus instructores.
 *
 *  El compliance no entra: los documentos son de la organización, no del
 *  site, y un socio con cuatro sites tiene un solo juego. Sumarlo en cada
 *  uno lo contaría cuatro veces. */
export function progresoDeSite(
  site: Site,
  instructoresDelSite: Instructor[],
  na: MarcasNA = SIN_MARCAS,
): Progreso {
  return sumar(
    progresoFicha('site', site.SiteId, site as unknown as Record<string, unknown>, na),
    ...instructoresDelSite.map((i) =>
      progresoFicha('instructor', i.InstructorId, i as unknown as Record<string, unknown>, na)),
  );
}

/** El progreso de una organización: lo suyo, lo de sus sites, lo de sus
 *  instructores y sus documentos, en un solo número.
 *
 *  `instructores` ya viene sin repetidos: quien esté en dos sites del mismo
 *  socio cuenta una vez. Repetirlo inflaría el denominador y haría que el
 *  mismo trabajo valiera el doble. */
export function progresoDeOrg(
  org: Organization,
  sitesDeLaOrg: Site[],
  instructores: Instructor[],
  compliance: ComplianceSummary | null | undefined,
  na: MarcasNA = SIN_MARCAS,
): Progreso {
  return sumar(
    progresoFicha('organization', org.OrgId, org as unknown as Record<string, unknown>, na),
    ...sitesDeLaOrg.map((s) =>
      progresoFicha('site', s.SiteId, s as unknown as Record<string, unknown>, na)),
    ...instructores.map((i) =>
      progresoFicha('instructor', i.InstructorId, i as unknown as Record<string, unknown>, na)),
    progresoCompliance(compliance),
  );
}

/** El número del panel: varias organizaciones en un solo porcentaje.
 *
 *  Suma numeradores y denominadores en vez de promediar los porcentajes.
 *  Promediarlos daría el mismo peso a un socio con un site que a uno con
 *  seis, y el panel dejaría de decir cuánto trabajo queda. */
export const progresoTotal = (partes: Progreso[]): Progreso =>
  partes.length ? sumar(...partes) : VACIO;

type Cuenta = { hechos: number; total: number };

/** De dónde sale el número de una organización, pieza a pieza.
 *
 *    total = ficha + Σ sites + compliance − repetidos
 *
 *  Cada site ya incluye a sus instructores. Quien imparte en dos sites del
 *  mismo socio está en los dos, pero en la organización cuenta una vez: esa
 *  diferencia es «repetidos», y es la ÚNICA. Se obtiene por resta para que
 *  la cuenta cierre siempre al campo; la prueba de cuadre comprueba además
 *  que coincide con los instructores realmente compartidos.
 *
 *  La calcula el servidor al guardar el porcentaje (migración 0016) y la
 *  demostración, que no tiene servidor, en memoria. */
export interface Desglose {
  ficha: Cuenta;
  sites: Cuenta;
  compliance: Cuenta;
  repetidos: Cuenta;
}

export function desgloseDe(ficha: Progreso, sites: Progreso[], compliance: Progreso, total: Progreso): Desglose {
  const c = (p: Cuenta): Cuenta => ({ hechos: p.hechos, total: p.total });
  const deSites = sites.reduce(
    (a, p) => ({ hechos: a.hechos + p.hechos, total: a.total + p.total }), { hechos: 0, total: 0 });
  return {
    ficha: c(ficha),
    sites: deSites,
    compliance: c(compliance),
    repetidos: {
      hechos: ficha.hechos + deSites.hechos + compliance.hechos - total.hechos,
      total: ficha.total + deSites.total + compliance.total - total.total,
    },
  };
}

/** Los campos de una ficha que siguen vacíos y no están excusados.
 *
 *  Para la pantalla de detalle: enseñar cuáles son es más útil que decir
 *  «te faltan nueve». */
export function camposPendientes(
  entidad: EntidadNA,
  id: string,
  record: Record<string, unknown> | null | undefined,
  na: MarcasNA = SIN_MARCAS,
): string[] {
  if (!record) return [];
  const excusados = new Set(na[entidad]?.[id] ?? []);
  return camposQueCuentan(entidad)
    .filter((c) => !excusados.has(c) && cuentaEnEstaFicha(c, record) && !tieneValor(c, record[c]));
}

/** ¿Está este campo marcado como «no aplica»? */
export const estaExcusado = (na: MarcasNA, entidad: EntidadNA, id: string, campo: string): boolean =>
  (na[entidad]?.[id] ?? []).includes(campo);
